///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProjectService } from '../../../services/project/project.service';
import { deleteProject } from '../store/project-modal-management/project-modal-management.actions';
import { Store } from '@ngrx/store';
import { finalize } from 'rxjs';
import { ProjectDTO } from '../../../generated/shared-types.model';
import { GtthEventManager } from '../../../services/event-manager.service';
import { LoaderIds, PreloaderService } from '../../../services/preloader.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-projects-delete-dialog',
    templateUrl: './delete-project-dialog.component.html',
    standalone: false
})
export class GtthProjectsDeleteDialogComponent implements OnInit {
    public project: ProjectDTO;
    public readonly loaderKey: LoaderIds = LoaderIds.DELETE_PROJECT_ID;

    constructor(
        private readonly store: Store,
        private readonly projectService: ProjectService,
        private readonly eventManager: GtthEventManager,
        public readonly preloaderService: PreloaderService,
        public dialogRef: MatDialogRef<GtthProjectsDeleteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { project: ProjectDTO }
    ) {}

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.confirmDelete();
        }
    }

    ngOnInit() {
        this.project = this.data.project;
    }

    confirmDelete() {
        this.preloaderService.show(this.loaderKey);

        this.projectService
            .deleteProject(this.project.id)
            .pipe(
                finalize(() => {
                    this.preloaderService.hide(this.loaderKey);
                })
            )
            .subscribe((response) => {
                if (!this.project.bugReport) {
                    this.store.dispatch(deleteProject());
                }

                if (response === null) {
                    this.eventManager.broadcast({ name: 'projectModified' });
                    this.dialogRef.close();
                }
            });
    }
}

@Component({
    selector: 'winde-projects-delete-popup',
    template: '',
    standalone: false
})
export class GtthProjectsDeletePopupComponent implements OnInit {
    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dialog: MatDialog,
        private readonly projectService: ProjectService
    ) {}

    ngOnInit() {
        const params = this.route.snapshot.paramMap;
        const id = params.has('id') ? parseInt(params.get('id'), 10) : undefined;

        if (isDefined(id)) {
            this.projectService.getProject(id).subscribe((project: ProjectDTO) => {
                if (!project) {
                    this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                } else {
                    const dialogRef = this.dialog.open(GtthProjectsDeleteDialogComponent, {
                        width: '800px',
                        position: {
                            top: '30px'
                        },
                        data: {
                            project
                        },
                        closeOnNavigation: true
                    });

                    dialogRef.afterClosed().subscribe(() => {
                        this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
                    });
                }
            });
        } else {
            this.router.navigate([{ outlets: { popup: null } }], { queryParamsHandling: 'preserve' });
        }
    }
}
